import { useEffect, useState, RefObject, useRef } from 'react'

export default function useOnScreen(ref: RefObject<HTMLElement | null> | undefined) {
  const [isIntersecting, setIntersecting] = useState(false)
  const observerRef = useRef<IntersectionObserver | null>(null)
  const observedElementRef = useRef<HTMLElement | null>(null)
  const rafRef = useRef<number | null>(null)

  // Setup or update observer - only acts if element changed
  const setupObserver = () => {
    const element = ref?.current ?? null

    if (element === observedElementRef.current) return

    // Cleanup previous observer if element changed
    if (observerRef.current) {
      observerRef.current.disconnect()
      observerRef.current = null
    }

    observedElementRef.current = element

    if (!element) {
      setIntersecting(false)
      return
    }

    // A busy main thread can get one callback for several changes, oldest first: the last entry
    // is where the element is now. The first left an embed scrolled into view marked hidden.
    observerRef.current = new IntersectionObserver((entries) => {
      setIntersecting(entries[entries.length - 1].isIntersecting)
    })
    observerRef.current.observe(element)
  }

  // Schedule setup with debounce using requestAnimationFrame
  const scheduleSetup = () => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current)
    }
    // requestAnimationFrame ensures DOM is painted before observing
    rafRef.current = requestAnimationFrame(() => {
      setupObserver()
      rafRef.current = null
    })
  }

  useEffect(() => {
    const element = ref?.current ?? null

    if (element !== observedElementRef.current) {
      scheduleSetup()
    }
  }) // Runs every render but only schedules when element changes

  useEffect(() => {
    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current)
      }
      if (observerRef.current) {
        observerRef.current.disconnect()
      }
    }
  }, [])

  return isIntersecting
}
